// writeRetry.js — queued upserts that retry on reconnect or the next app
// resume (2026-10-23). Used by healthSync's daily_activity, workouts and
// sleep_nights upserts: a network failure queues the rows (last write wins
// per date / external id) instead of reporting an error; Sentry hears about
// it only when the retries are exhausted. Per-user localStorage, cleared with
// the rest of the user's storage on sign-out.
import supabase from '../supabase';
import { reportError } from '../sentry';
import { enqueueRows, groupForFlush, afterFlush, isTransportFailure, WRITE_QUEUE_MAX_ATTEMPTS } from '../lib/writeQueue';

const keyFor = (uid) => `scorify_${uid}_write_queue_v1`;
const read = (uid) => { try { const v = JSON.parse(localStorage.getItem(keyFor(uid)) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; } };
const write = (uid, q) => { try { if (q.length) localStorage.setItem(keyFor(uid), JSON.stringify(q)); else localStorage.removeItem(keyFor(uid)); } catch {} };

/**
 * Upsert now; on a transport failure queue the rows for later. Returns
 * { ok } | { queued: true } | { error } (a server rejection, for the caller to report).
 */
export async function queueUpsert({ userId, table, conflict, keyOf, rows }) {
  if (!rows || !rows.length) return { ok: true };
  try {
    const { error } = await supabase.from(table).upsert(rows, { onConflict: conflict, ignoreDuplicates: false });
    if (!error) return { ok: true };
    if (!isTransportFailure(error)) return { error };
    write(userId, enqueueRows(read(userId), { table, conflict, keyOf, rows }));
    console.warn(`[writeRetry] ${table}: network failure, ${rows.length} row(s) queued`);
    return { queued: true };
  } catch (e) {
    if (!isTransportFailure(e)) return { error: e };
    write(userId, enqueueRows(read(userId), { table, conflict, keyOf, rows }));
    console.warn(`[writeRetry] ${table}: network failure, ${rows.length} row(s) queued`);
    return { queued: true };
  }
}

let flushing = false;
/** Retry everything queued for this user. Safe to call often; no-op when empty or offline. */
export async function flushWriteQueue(userId) {
  if (!userId || flushing) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  let queue = read(userId);
  if (!queue.length) return;
  flushing = true;
  try {
    for (const group of groupForFlush(queue)) {
      let outcome = 'ok';
      try {
        const { error } = await supabase.from(group.table).upsert(group.entries.map(e => e.row), { onConflict: group.conflict, ignoreDuplicates: false });
        if (error) outcome = isTransportFailure(error) ? 'transport' : 'rejected';
        if (outcome === 'rejected') reportError(new Error(`${group.table} queued upsert rejected: ${error.message}`), { op: 'writeRetry.rejected', table: group.table, rows: group.entries.length });
      } catch (e) { outcome = isTransportFailure(e) ? 'transport' : 'rejected'; }
      const r = afterFlush(queue, group, outcome);
      queue = r.queue;
      if (r.exhausted.length) {
        reportError(new Error(`${group.table} write queue exhausted after ${WRITE_QUEUE_MAX_ATTEMPTS} attempts`), { op: 'writeRetry.exhausted', table: group.table, rows: r.exhausted.length, level: 'error' });
      }
      write(userId, queue);
      if (outcome === 'transport') break; // still no network: try again on the next reconnect/resume
    }
  } finally { flushing = false; }
}

let installedFor = null;
/** Flush on reconnect and on the next resume. Idempotent per user. */
export function installWriteRetry(userId) {
  if (!userId || installedFor === userId || typeof window === 'undefined') return;
  installedFor = userId;
  window.addEventListener('online', () => flushWriteQueue(userId));
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') flushWriteQueue(userId); });
}
